import Image from "next/image";
import { useRouter } from "next/router";
import { useTranslation } from "next-i18next";

import { Text } from "@/components/elements/Text";
import useProductDetailsPage from "@/hooks/useProductDetailsPage";
import { I18nextLocale } from "@/types/types";
import { isLargeContainer } from "@/utils";
import { isWebshopClosed } from "@/utils/helpers";

import { Button, Message } from "../elements";
import { ProductLogo } from "./ProductLogo";
import styles from "./styles.module.css";

type ProductDetailsProp = {
  title: string;
  price: string;
  pricePerLitre: string;
  brand: {
    name: string;
    logo?: string;
  };
  size: number;
  size_original: number;
  relativePackaging: number;
  swan_license_number?: string;
};

export const ProductDetails = ({
  productDetails,
}: {
  productDetails: ProductDetailsProp;
}) => {
  const { t } = useTranslation("product");
  const { handleRelativePackagingInfoClick } = useProductDetailsPage();
  const {
    title,
    price,
    brand,
    pricePerLitre,
    relativePackaging,
    size_original,
    size,
    swan_license_number,
  } = productDetails;
  const router = useRouter();

  const containerIcon = isLargeContainer(size)
    ? "container_lg"
    : "container_sm";

  const swanLogo =
    router.locale === I18nextLocale.EN_NO
      ? "/logos/UK_logo.svg"
      : "/logos/NO_logo.svg";

  return (
    <section>
      <div className={styles.topBar} data-testid="top-bar">
        <div className={styles.logos}>
          <ProductLogo src={brand.logo} alt={brand.name} />
          {swan_license_number ? (
            <div className={styles.swanLogo}>
              <Image
                src={swanLogo}
                alt={t("swan_logo.title")}
                width={70}
                height={70}
              />
              <span className={styles.licenseNumber}>
                {swan_license_number}
              </span>
            </div>
          ) : null}
        </div>
      </div>
      <div className={styles.productDetails}>
        <div className={styles.titlePrice}>
          <Text as="h1" size="m">
            {title}
          </Text>
          {isWebshopClosed ? null : <p>{price}</p>}
        </div>
        <div className={styles.brandSize}>
          <p className={styles.brand}>{brand.name}</p>
          {isWebshopClosed ? null : <p>{pricePerLitre}</p>}
        </div>
      </div>
      <Message icon={containerIcon} className={styles.message}>
        <span data-testid="product-size-info">
          {t("packaging", {
            containerSize: size,
            relativePackaging: Math.round(relativePackaging),
            bottleSize: size_original,
          })}
        </span>
        <Button
          theme="underline"
          onClick={() =>
            handleRelativePackagingInfoClick(
              relativePackaging,
              size_original,
              size
            )
          }
        >
          {t("view_details")}
        </Button>
      </Message>
    </section>
  );
};
